<template>
  <v-card rounded="lg">
    <v-card-title class="d-flex justify-space-between align-center">
      <div class="title-container">
        <div class="text-h5 font-weight-black ps-2">
          {{ $t('i18n.preview') }}
        </div>
        <div class="text-subtitle-1 font-weight-regular ps-2">
          {{ formObject.form.name }}
        </div>
      </div>
      <!-- Toolbar do Modal (Botões de Next, Reset e Close) -->
      <div>
        <v-tooltip v-if="showNextButton" :text="$t('i18n.next')" location="start">
          <template v-if="showNextButton" v-slot:activator="{ props }">
            <v-btn v-bind="props" icon="mdi-chevron-right" variant="text" @click="nextFormItem()" size="x-large"></v-btn>
          </template>
        </v-tooltip>
        <v-tooltip :text="$t('i18n.resetPreview')" location="start">
          <template v-slot:activator="{ props }">
            <v-btn v-bind="props" icon="mdi-restore" variant="text" @click="resetPreview" size="x-large"></v-btn>
          </template>
        </v-tooltip>
        <v-btn icon="mdi-close" variant="text" @click="closePreview" size="x-large"></v-btn>
      </div>
    </v-card-title>

    <v-card-text class="overflow-y-auto preview-body text-white">
      <PreviewAppTopBar />
      <div class="scrollable-container">
        <!-- all_on_one_page -->
        <div v-if="!showThankYouMessage && formObject.form.layout_mode === 'all_on_one_page'">
          <div v-for="(formItem, index) in formObject.form_items" :key="formItem.id">
            <component :is="getComponentName(formItem)" v-bind="getComponentProps(formItem)" :key="refreshKey" />
            <div v-if="formObject.form_items.length > 1 && Number(index) < formObject.form_items.length - 1" class="separator">
            </div>
          </div>
        </div>
        <!-- one_per_page -->
        <div v-else-if="!showThankYouMessage && formObject.form.layout_mode === 'one_per_page'">
          <div v-if="currentFormItemIndex < formObject.form_items.length">
            <div v-for="textLabelItem in formItemsToDisplay.consecutiveTextLabels" :key="textLabelItem.id">
              <component :is="getComponentName(textLabelItem)" v-bind="getComponentProps(textLabelItem)" :key="refreshKey" />
              <div class="separator"></div>
            </div>
            <component v-if="formItemsToDisplay.nextInteractiveItem" :is="getComponentName(formItemsToDisplay.nextInteractiveItem)" v-bind="getComponentProps(formItemsToDisplay.nextInteractiveItem)" :key="refreshKey" />
          </div>
        </div>
        <!-- Thank you message -->
        <ThankYouForParticipating v-if="showThankYouMessage" :bilingual="propBilingual" />
      </div>
      <!-- <PreviewFooterBar /> -->
    </v-card-text>

    <v-card-actions>
      <v-btn :text="$t('i18n.closeBtnText')" @click="closePreview" variant="outlined"></v-btn>
    </v-card-actions>
  </v-card>
</template>

<script lang="ts">
import { FormPreviewProps, FormItem } from '@/utils/DataTypes'
import { defineComponent } from 'vue'

import PreviewAppTopBar from '../misc/form_preview/PreviewAppTopBar.vue'
/* import PreviewFooterBar from '../misc/form_preview/PreviewFooterBar.vue' */
import RatingFaces from '@/components/misc/form_preview/RatingFaces.vue'
import RatingStars from '@/components/misc/form_preview/RatingStars.vue'
import MultiChoice from '@/components/misc/form_preview/MultiChoice.vue'
import NavigationItem from '@/components/misc/form_preview/NavigationItem.vue'
import TextLabel from '@/components/misc/form_preview/TextLabel.vue'
import TextInput from '@/components/misc/form_preview/TextInput.vue'
import GridItem from '@/components/misc/form_preview/GridItem.vue'
import ThankYouForParticipating from '@/components/misc/form_preview/ThankYouForParticipating.vue'

export default defineComponent({
  name: 'FormPreview',
  components: {
    PreviewAppTopBar,
    /* PreviewFooterBar, */
    RatingFaces,
    RatingStars,
    MultiChoice,
    NavigationItem,
    TextLabel,
    TextInput,
    GridItem,
    ThankYouForParticipating
  },
  props: {
    propBilingual: {
      type: Boolean,
      required: false,
      default: false
    },
    formObject: {
      type: Object,
      default: () => {
        return {
          associatedDeviceIDs: [] as number[],
          form: {
            name: '',
            description: '',
            layout_mode: 'all_on_one_page'
          },
          form_items: [] as FormItem[]
        }
      }
    }
  },
  data() {
    return {
      currentFormItemIndex: 0, // Índice do form item atual (aplicável apenas quando layout_mode = 'one_per_page')
      refreshKey: 0,

      showThankYouMessage: false
    }
  },
  methods: {
    resetPreview() {
      this.currentFormItemIndex = 0
      this.refreshKey += 1
      this.showThankYouMessage = false
    },
    closePreview() {
      this.$emit('close-preview')
    },
    getComponentName(formItem: FormItem) {
      switch (formItem.name) {
        case 'RATING':
          if (formItem.rating_type_id === 1) {
            return 'RatingStars'
          } else if (formItem.rating_type_id === 2) {
            return 'RatingFaces'
          } else {
            return null
          }
        case 'MULTI_CHOICE':
          return 'MultiChoice'
        case 'GRID':
          return 'GridItem'
        case 'NAVIGATION':
          return 'NavigationItem'
        case 'TEXT_LABEL':
          return 'TextLabel'
        case 'TEXT_INPUT':
          return 'TextInput'
        default:
          return null
      }
    },
    getComponentProps(formItem: FormItem): FormPreviewProps {
      const props: FormPreviewProps = {
        form_item_order: formItem.form_item_order ?? 0,
        bilingue: formItem.bilingual
      }

      if (formItem.name === 'RATING') {
        props.question_lang1 = formItem.question_lang1
        props.question_lang2 = formItem.question_lang2
      } else if (formItem.name === 'NAVIGATION') {
        props.options = formItem.form_item_options
        props.question_lang1 = formItem.question_lang1
        props.question_lang2 = formItem.question_lang2
      } else if (formItem.name === 'MULTI_CHOICE') {
        props.options = formItem.form_item_options
        props.question_lang1 = formItem.question_lang1
        props.question_lang2 = formItem.question_lang2
      } else if (formItem.name === 'GRID') {
        props.options = formItem.form_item_options
        props.question_lang1 = formItem.question_lang1
        props.question_lang2 = formItem.question_lang2
      } else if (formItem.name === 'TEXT_LABEL') {
        props.text_label_lang1 = formItem.text_label_lang1
        props.text_label_lang2 = formItem.text_label_lang2
      } else if (formItem.name === 'TEXT_INPUT') {
        props.question_lang1 = formItem.question_lang1
        props.question_lang2 = formItem.question_lang2
      }

      return props
    },
    isTextLabel(formItem: FormItem): boolean {
      return formItem.name === 'TEXT_LABEL'
    },
    isNavigationItem(formItem: FormItem | null): boolean {
      return formItem?.name === 'NAVIGATION'
    },
    nextFormItem() {
      if (this.formObject.form.layout_mode === 'one_per_page') {
        this.refreshKey++
        this.currentFormItemIndex += this.formItemsToDisplay.consecutiveTextLabels.length
        if (this.formItemsToDisplay.nextInteractiveItem) {
          this.currentFormItemIndex++

          if (this.currentFormItemIndex >= this.formObject.form_items.length) {
            this.showThankYouMessage = true
          }
        } else {
          this.showThankYouMessage = true
        }
      } else {
        this.showThankYouMessage = true
      }
    }
  },
  computed: {
    formItemsToDisplay() {
      const formDisplayData = {
        consecutiveTextLabels: [] as Array<FormItem>,
        nextInteractiveItem: null as null | FormItem,
        nextInteractiveItemIndex: -1,
        isNavigationItem: false
      }

      let index = this.currentFormItemIndex

      while (index < this.formObject.form_items.length && this.isTextLabel(this.formObject.form_items[index])) {
        formDisplayData.consecutiveTextLabels.push(this.formObject.form_items[index])
        index++
      }

      if (index < this.formObject.form_items.length) {
        formDisplayData.nextInteractiveItem = this.formObject.form_items[index]
        formDisplayData.nextInteractiveItemIndex = index
        formDisplayData.isNavigationItem = this.isNavigationItem(this.formObject.form_items[index])
      } else {
        formDisplayData.nextInteractiveItem = null
      }

      return formDisplayData
    },
    showNextButton() {
      if (this.showThankYouMessage) return false

      const currentIndex = this.formObject.form.layout_mode === 'all_on_one_page'
        ? this.currentFormItemIndex
        : this.formItemsToDisplay.nextInteractiveItemIndex

      return !this.isNavigationItem(this.formObject.form_items[currentIndex])
    }
  }
})
</script>

<style lang="scss" scoped>
.title-container {
  display: flex;
  flex-direction: column;
}

.preview-body {
  display: flex;
  flex-direction: column;
  height: 100%;

  font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif; // Igual ao do tablet e do mobile

  background-image: url('../../assets/login-bg.png');
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;

  .scrollable-container {
    overflow-y: auto;
    flex-grow: 1;
    height: 30em;
    width: 100%;
    scrollbar-width: none;
    /* Hide scrollbar in Firefox */
  }

  .scrollable-container::-webkit-scrollbar {
    display: none;
    /* Hide scrollbar in Webkit browsers */
    width: 1em;
  }

  .custom-scroll::-webkit-scrollbar-track {
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  }

  .custom-scroll::-webkit-scrollbar-thumb {
    background-color: darkgrey;
    outline: 1px solid slategrey;
  }
}

.separator {
  width: 20%;
  height: 2px;

  /* Color of the line */
  background-color: #ddd;

  /* Space around the line */
  margin: 40px 0;
  margin-left: 40%;
}
</style>
